// O chamado, dentro do painel de contexto.
//
// Mostra o protocolo (que é o que o cliente cita), o SLA e o caminho para a TI.
// Quando o chamado atravessa canais, o contador de conversas vinculadas é o que
// avisa que existe conversa em outro lugar respondendo pelo mesmo número — sem
// isso, o atendente responde no WhatsApp sem saber que o assunto já continuou
// por e-mail.

import React, { useState } from 'react';
import {
  ArrowUpRight, CheckCircle2, Clock, ExternalLink, Link2, Loader2, Ticket, TimerOff,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import type { Chamado, TicketStatus } from '../tipos';
import { prazoRelativo } from '../formato';

interface Props {
  chamado: Chamado;
  ocupado: boolean;
  onMudarStatus: (s: TicketStatus) => void;
  onEscalar: () => void;
}

const ROTULO: Record<TicketStatus, string> = {
  aberto: 'Aberto',
  em_andamento: 'Em andamento',
  aguardando_cliente: 'Aguardando cliente',
  resolvido: 'Resolvido',
  fechado: 'Fechado',
};

const CLASSE: Record<TicketStatus, string> = {
  aberto: 'bg-secondary/20 text-[#9A6A12] dark:text-secondary',
  em_andamento: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
  aguardando_cliente: 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-300',
  resolvido: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  fechado: 'bg-gray-100 text-gray-500 dark:bg-slate-700 dark:text-slate-400',
};

const PROXIMOS: TicketStatus[] = ['aberto', 'em_andamento', 'aguardando_cliente', 'resolvido'];

export default function PainelChamado({ chamado, ocupado, onMudarStatus, onEscalar }: Props) {
  const [abrirStatus, setAbrirStatus] = useState(false);
  const encerrado = chamado.status === 'resolvido' || chamado.status === 'fechado';

  const slaResposta = prazoRelativo(chamado.prazo_primeira_resposta);
  const slaResolucao = prazoRelativo(chamado.prazo_resolucao);
  const respondido = !!chamado.primeira_resposta_em;

  return (
    <div className="border-b border-gray-200 px-4 py-3.5 dark:border-slate-700">
      <h4 className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-gray-400 dark:text-slate-500">
        <Ticket size={12} />
        Chamado
      </h4>

      <div className="rounded-xl border border-gray-200 px-3 py-2.5 dark:border-slate-700">
        <div className="flex items-center justify-between gap-2">
          <span className="font-mono text-xs font-bold tracking-tight text-gray-900 dark:text-white">
            {chamado.protocolo}
          </span>
          <div className="relative">
            <button
              onClick={() => setAbrirStatus((v) => !v)}
              disabled={ocupado}
              className={`rounded-full px-2 py-0.5 text-[11px] font-semibold transition-opacity hover:opacity-80 disabled:opacity-50 ${CLASSE[chamado.status]}`}
            >
              {ocupado ? '…' : ROTULO[chamado.status]}
            </button>
            {abrirStatus && (
              <div className="absolute right-0 top-full z-20 mt-1 w-44 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-lg dark:border-slate-600 dark:bg-slate-800">
                {PROXIMOS.map((s) => (
                  <button
                    key={s}
                    onClick={() => { setAbrirStatus(false); onMudarStatus(s); }}
                    className={`block w-full px-3 py-1.5 text-left text-xs transition-colors hover:bg-gray-100 dark:hover:bg-slate-700 ${
                      s === chamado.status ? 'font-bold text-primary' : 'text-gray-600 dark:text-slate-300'
                    }`}
                  >
                    {ROTULO[s]}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <p className="mt-1 text-xs font-medium text-gray-700 dark:text-slate-200">{chamado.assunto}</p>

        {!encerrado && (
          <div className="mt-2 space-y-1">
            <p className={`flex items-center gap-1.5 text-[11px] ${
              respondido ? 'text-emerald-600 dark:text-emerald-400'
                : slaResposta.estourado ? 'text-red-600 dark:text-red-400'
                : 'text-gray-500 dark:text-slate-400'
            }`}>
              {respondido ? <CheckCircle2 size={11} /> : slaResposta.estourado ? <TimerOff size={11} /> : <Clock size={11} />}
              1ª resposta: {respondido ? 'feita' : slaResposta.texto}
            </p>
            <p className={`flex items-center gap-1.5 text-[11px] ${
              slaResolucao.estourado ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-slate-400'
            }`}>
              {slaResolucao.estourado ? <TimerOff size={11} /> : <Clock size={11} />}
              Resolver: {slaResolucao.texto}
            </p>
          </div>
        )}

        {(chamado.conversas_vinculadas ?? 1) > 1 && (
          <p className="mt-2 flex items-center gap-1.5 rounded-lg bg-gray-50 px-2 py-1.5 text-[11px] text-gray-600 dark:bg-slate-900/50 dark:text-slate-300">
            <Link2 size={11} className="shrink-0" />
            {chamado.conversas_vinculadas} conversas respondem por este protocolo
          </p>
        )}

        {chamado.responsavel_nome && (
          <p className="mt-2 text-[11px] text-gray-500 dark:text-slate-400">
            Responsável: {chamado.responsavel_nome}
          </p>
        )}
      </div>

      {chamado.tarefa_id ? (
        <Link
          to="/tarefas/solicitacoes"
          className="group mt-2 flex items-center gap-2 rounded-xl bg-gray-50 px-3 py-2 text-[11px] text-gray-600 transition-colors hover:bg-gray-100 dark:bg-slate-900/50 dark:text-slate-300 dark:hover:bg-slate-900"
        >
          <ArrowUpRight size={12} className="shrink-0 text-primary" />
          <span className="flex-1">Escalado para a TI — solicitação #{chamado.tarefa_id}</span>
          <ExternalLink size={11} className="shrink-0 text-gray-300 group-hover:text-primary dark:text-slate-600" />
        </Link>
      ) : (
        <button
          onClick={onEscalar}
          disabled={ocupado}
          className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-3 py-2 text-[11px] font-semibold text-gray-600 transition-colors hover:border-primary/40 hover:text-primary disabled:opacity-50 dark:border-slate-700 dark:text-slate-300"
        >
          {ocupado ? <Loader2 size={11} className="animate-spin" /> : <ArrowUpRight size={11} />}
          Escalar para a TI
        </button>
      )}
    </div>
  );
}
